import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { noop } from 'rxjs';
import { DropdownExpandDirection } from 'libs/core/src/lib/enum/dropdown-expand-direction.enum';
import { MobileDeviceUtils } from 'libs/core/src/lib/utilities/mobile-device-utils';
import { ControlValueAccessor } from '@angular/forms';

@Component({
  template: '',
  standalone: true,
  imports: []
})
export class DropdownComponent implements OnInit, ControlValueAccessor {
  DropdownExpandDirection = DropdownExpandDirection;
  _options: Array<any>;
  private automaticallySelectedOptionId: unknown;

  @Input() set options(value: Array<any>) {
    this._options = value ? [...value] : [];

    const singleOptionId = this._options[0]?.id;
    if (this.autoSelectSingleOption && this._options.length === 1 && this.automaticallySelectedOptionId !== singleOptionId) {
      this.selected = 0;
      this.automaticallySelectedOptionId = singleOptionId;
      setTimeout(() => {
        if (this._options.length !== 1 || this._options[0]?.id !== singleOptionId) {
          return;
        }

        this.onChangeCallback(singleOptionId);
        this.onTouchedCallback();
      });
    } else if (this._options.length !== 1) {
      this.automaticallySelectedOptionId = undefined;
    }
  }

  @Input() className: string;
  @Input() isReadOnly = false;
  @Input() pristineMessage: string = ' ';
  @Input() canBeToggled = true;
  @Input() expandDirection = DropdownExpandDirection.Down;
  @Input() computeExpandDirection = false;
  @Input() dropdownItemTemplate: TemplateRef<any>;
  @Input() selectedItemTemplate: TemplateRef<any>;
  @Input() chevronStickedRight = true;
  @Input() autoSelectSingleOption = true;
  @Input() beforeOptSelect = () => {
    return true;
  };
  @Output() optSelect = new EventEmitter();
  @Output() toggleClicked = new EventEmitter();

  @ViewChild('dropdownElement') dropdownElement: ElementRef;
  @ViewChild('dropdownWrapper') dropdownWrapper: ElementRef;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: unknown) => void = noop;

  private _selectedTemporary;
  private _selected;

  get selected(): number {
    return this._selected;
  }

  set selected(value: number) {
    this.pristine = value === null || value === -1;
    this._selected = value;
    this._selectedTemporary = value;
  }

  protected computedExpandDirection = DropdownExpandDirection.Down;

  isOpen = false;
  public pristine = true;

  isFocused: boolean = false;
  disabled = false;

  get hasMoreItems() {
    return this._options.length > 1 || !this.autoSelectSingleOption;
  }

  get canSelect() {
    return this._options?.length === 1 && this.autoSelectSingleOption;
  }

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {}

  @HostListener('keydown', ['$event'])
  keyPressHandle(event: KeyboardEvent) {
    if (this.disabled || this.isReadOnly) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        if (this.isOpen) {
          event.stopPropagation();
          this.isOpen = false;
          this.focusTrigger();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen && this.canBeToggled) {
          this.toggle(event);
        } else if (this._options.length > 0) {
          const nextIndex = this._selected === -1 ? 0 : Math.min(this._selected + 1, this._options.length - 1);
          this.updateSelectionAndActiveDescendant(nextIndex);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen && this._options.length > 0) {
          const prevIndex = Math.max(this._selected - 1, 0);
          this.updateSelectionAndActiveDescendant(prevIndex);
        }
        break;
      case 'Home':
        event.preventDefault();
        if (this.isOpen && this._options.length > 0) {
          this.updateSelectionAndActiveDescendant(0);
        }
        break;

      case 'End':
        event.preventDefault();
        if (this.isOpen && this._options.length > 0) {
          this.updateSelectionAndActiveDescendant(this._options.length - 1);
        }
        break;

      case 'Enter':
      case ' ': // Spacja
        if (this.isOpen && this._selected > -1) {
          event.preventDefault();
          this.acceptSelected();
          this.isOpen = false;
          this.focusTrigger();
        } else if (!this.isOpen && event.key === ' ') {
          event.preventDefault();
          this.toggle(event);
        }
        break;

      case 'Tab':
        if (this.isOpen) {
          this.isOpen = false;
          this.onTouchedCallback();
        }
        break;
    }
  }

  private updateSelectionAndActiveDescendant(index: number) {
    this._selected = index;
    this._selectedTemporary = index;

    this.scrollToOption(index);
  }

  private scrollToOption(index: number) {
    const el = this.elementRef.nativeElement.querySelector(`#li${index}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }

  private focusTrigger() {
    const trigger = this.elementRef.nativeElement.querySelector('.box');
    if (trigger) {
      trigger.focus();
    }
  }

  focusHandler() {
    this.isFocused = true;
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    // relatedTarget is unreliable with some touchpad/trackpad tap gestures (comes back null even
    // though focus is moving to an option inside this dropdown), so don't close synchronously on it.
    // Defer to the next tick and check document.activeElement instead, giving the option's click
    // handler time to run and register the selection before we decide to close.
    if (event.relatedTarget && this.elementRef.nativeElement.contains(event.relatedTarget)) {
      return;
    }

    setTimeout(() => {
      if (!this.elementRef.nativeElement.contains(document.activeElement)) {
        this.isFocused = false;
        this.isOpen = false;
        this.onTouchedCallback();
      }
    });
  }

  focusOutHandler() {
    // `dropdownElement` (@ViewChild) only exists in the webcomponent dropdown template; in every
    // other app template it's always undefined, which made `!this.dropdownElement?.nativeElement...`
    // permanently truthy and force-closed the dropdown ~100ms after ANY blur, including the blur
    // caused by clicking an option - randomly swallowing selections that took a bit longer to land.
    setTimeout(() => {
      if (!this.elementRef.nativeElement.contains(document.activeElement)) {
        this.isFocused = false;
        this.isOpen = false;
        this.onTouchedCallback();
      }
    }, 100);
  }

  private acceptSelected() {
    if (this._options[this._selected]) {
      this.onChangeCallback(this._options[this._selected].id);
      this.onTouchedCallback();
      this.optSelect.emit(this._options[this._selected]);
    }
  }

  optionSelect(selectedOption: any, idx: number, e: Event) {
    // ignore non-primary buttons since this now runs on mousedown (fires for right/middle click too)
    if (e instanceof MouseEvent && e.button !== 0) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    if (!this.beforeOptSelect()) {
      this.isOpen = false;
      return;
    }

    this.selected = idx;
    this.isOpen = false;
    this.onChangeCallback(selectedOption?.id);
    this.onTouchedCallback();
    this.optSelect.emit(selectedOption);

    this.focusTrigger();
  }

  toggle(e: Event) {
    e.stopPropagation();

    if (this.disabled || this.isReadOnly) {
      return;
    }

    this.selectDirection();

    if (!this._options.length || (this._options.length === 1 && this.autoSelectSingleOption)) {
      return;
    }

    this.toggleClicked.emit(this._options[this.selected]);

    if (!this.canBeToggled) {
      return;
    }

    const allElems = document.querySelectorAll('.dropdown-wrapper');
    allElems.forEach((el) => {
      if (el !== this.elementRef.nativeElement.querySelector('.dropdown-wrapper')) {
        el.classList.remove('is-open');
      }
    });

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.isFocused = true;
      if (!this._selected) {
        this._selected = -1;
      }

      if (this._selected === -1 && this._options.length > 0) {
        this._selected = 0;
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.isFocused = false;
    }
  }

  writeValue(obj: any): void {
    if (obj !== undefined && obj !== null && obj !== '') {
      const index = this._options?.findIndex((y) => y.id === obj) ?? -1;
      this.selected = index < 0 ? -1 : index;
    } else {
      this.selected = -1;
    }
  }

  registerOnChange(fn: (_: unknown) => void) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouchedCallback = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  isSelectable() {
    return (!this.selected || this._options.length > 1) && this.canBeToggled;
  }

  selectDirection() {
    if (MobileDeviceUtils.isMobile()) {
      this.computedExpandDirection = DropdownExpandDirection.Down;
      return;
    }

    if (!this.computeExpandDirection) {
      this.computedExpandDirection = this.expandDirection;
    } else if (
      this.computeExpandDirection &&
      this.dropdownWrapper?.nativeElement.getBoundingClientRect().top < this.dropdownElement?.nativeElement.offsetHeight
    ) {
      this.computedExpandDirection = DropdownExpandDirection.Down;
    } else {
      this.computedExpandDirection = DropdownExpandDirection.Up;
    }
  }
}
